import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IonInfiniteScroll } from '@ionic/angular';
import { interval, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AbstractComponentWithScrollContainer } from 'src/app/shared/components/abstract-component-with-scroll-container';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { BasicService } from 'src/app/shared/services/basic.service';
import { DateDifferenceService } from 'src/app/shared/services/date-difference.service';
import { ScrollService } from 'src/app/shared/services/scroll.service';

import { taskIdFromTaskOrActionId } from '../../shared/id-conversions';
import { Task } from '../../shared/model';
import { TaskProcessContext } from '../../shared/task-process-context.class';
import { TaskProcessService } from '../../shared/task-process.service';
import { TaskPublicContext } from './task-public-context';

@Component({
  selector: 'app-public-context',
  templateUrl: './public-context.component.html',
  styleUrls: ['./public-context.component.scss'],
})
export class PublicContextComponent extends AbstractComponentWithScrollContainer implements OnInit, OnDestroy {
  @Input() taskProcessContext: TaskProcessContext;
  @Input() task: Task;
  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;

  taskPublicContext: TaskPublicContext;
  private isTwitterPublicTask: boolean;

  private taskId: number;
  private ngOnDestroy$ = new Subject();

  constructor(
    private basicService: BasicService,
    private dateDifferenceService: DateDifferenceService,
    private scrollService: ScrollService,
    private taskProcessService: TaskProcessService
  ) {
    super();
  }

  ngOnInit() {
    this.taskId = taskIdFromTaskOrActionId(this.task.id);
    this.taskPublicContext = new TaskPublicContext(
      this.taskId,
      this.dateDifferenceService,
      this.taskProcessService,
      this.taskProcessContext
    );

    this.isTwitterPublicTask =
      this.taskProcessContext.site.type === SiteTypeEnum.Twitter &&
      !this.taskProcessContext.processedTaskInfo.isPrivate;

    if (!this.isTwitterPublicTask) {
      this.taskPublicContext.loadContextAndInitialMessages().subscribe(() => {
        const elementId = this.taskProcessService.bubbleElementId(this.taskPublicContext.processedMessage.id);

        // show 48 px above the message, to let the user know if there is a message above
        setTimeout(() => this.scrollService.scrollIonContentToElement(this, elementId, 48));
        this.updateInfiniteScrollDisabled();
      });
    }

    interval(10000)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.taskPublicContext.updateMessagesTimeDelta());
  }

  ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  get loadNMessagesLabel() {
    const n = String(this.taskPublicContext.sizeOfNextChunkOfMessagesBefore());
    return this.basicService.translate('m/task-process/public-context/modal-load-n-messages', { n });
  }

  loadMessagesBefore() {
    this.basicService.showSpinner();
    this.taskPublicContext
      .loadMessagesBefore()
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe();
  }

  loadMessagesAfter(event) {
    this.taskPublicContext
      .loadMessagesAfter()
      .pipe(
        finalize(() => {
          event.target.complete(); // let ion-infinite-scroll know we're done loading
          this.updateInfiniteScrollDisabled();
        })
      )
      .subscribe();
  }

  private updateInfiniteScrollDisabled() {
    this.infiniteScroll.disabled = !this.taskPublicContext.canLoadMessagesAfter();
  }
}
